import React, { useEffect, useState } from "react";
import { getReferralDocuments, viewDocumentInline } from "../APIs/referralApi";

const TYPE_LABEL = {
  REFERRAL_DOCUMENT: "Referral Document",
  CLINICAL_NOTES: "Clinical Notes",
  LAB_RESULTS: "Lab Results",
  IMAGING: "Imaging",
};

function EyeIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg
      className="w-4 h-4 text-gray-400 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

export default function ReferralDocumentsSection({ referralId }) {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState({});

  useEffect(() => {
    if (docs !== null) return;
    setLoading(true);
    getReferralDocuments(referralId)
      .then(setDocs)
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [referralId, docs]);

  return (
    <div className="pt-3 mt-2 border-t">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Referral Documents
      </p>

      {loading && <p className="text-xs text-gray-400 py-1">Loading...</p>}

      {!loading && docs !== null && docs.length === 0 && (
        <p className="text-xs text-gray-400 py-1">No documents attached.</p>
      )}

      {!loading && docs && docs.length > 0 && (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[1fr_140px_120px_48px] bg-gray-50 border-b border-gray-200 px-3 py-2 text-xs font-medium text-gray-500">
            <span className="pl-6">Document Name</span>
            <span>Type</span>
            <span>Uploaded On</span>
            <span className="text-center">Action</span>
          </div>

          {/* Rows */}
          {docs.map((doc, i) => (
            <div
              key={doc.documentId}
              className={`grid grid-cols-[1fr_140px_120px_48px] items-center px-3 py-2.5 text-sm ${
                i < docs.length - 1 ? "border-b border-gray-100" : ""
              }`}
            >
              {/* Document Name */}
              <div className="flex items-center gap-2 min-w-0">
                <DocIcon />
                <span className="text-gray-800 truncate">{doc.fileName}</span>
              </div>

              {/* Type */}
              <span className="text-gray-500 text-xs">
                {TYPE_LABEL[doc.category] ?? doc.category ?? "—"}
              </span>

              {/* Uploaded On */}
              <span className="text-gray-500 text-xs">
                {doc.uploadTime
                  ? new Date(doc.uploadTime).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "—"}
              </span>

              {/* Action */}
              <div className="flex justify-center">
                <button
                  disabled={viewing[doc.documentId]}
                  onClick={async () => {
                    setViewing((v) => ({ ...v, [doc.documentId]: true }));
                    try {
                      const url = await viewDocumentInline(doc.documentId);
                      window.open(url, "_blank", "noopener,noreferrer");
                    } catch {
                      alert("Unable to load document. Please try again.");
                    } finally {
                      setViewing((v) => ({ ...v, [doc.documentId]: false }));
                    }
                  }}
                  className="text-green-600 hover:text-green-700 transition disabled:opacity-40"
                  title="View document"
                >
                  <EyeIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
